import { usePage } from '@inertiajs/vue3';

/**
 * Browser-side telemetry batcher for browser_webrtc publisher + viewer
 * events. Emit events from anywhere (`createStreamingTelemetrySession`),
 * the singleton flushes them in batches to /api/streaming/telemetry every
 * `FLUSH_INTERVAL_MS` or on page hide via `navigator.sendBeacon`.
 *
 * Telemetry never blocks the application: network errors are swallowed,
 * oversize buffers drop oldest events, and the module is safe to import
 * from server-rendered code (the `window` listeners no-op when undefined).
 */

type ActorRole = 'publisher' | 'viewer';

interface TelemetrySessionContext {
	sessionId: string;
	enrollmentTestId: number | null;
	actor: ActorRole;
}

interface QueuedEvent {
	session_id: string;
	enrollment_test_id: number | null;
	actor: ActorRole;
	event: string;
	at_client: string;
	page: string | null;
	details: Record<string, unknown>;
}

const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER = 200;
const MAX_BATCH = 50;
const ENDPOINT = '/api/streaming/telemetry';

const buffer: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pageHideRegistered = false;

function getCurrentPage(): string | null {
	try {
		const page = usePage() as unknown as { component?: string };
		return page?.component ?? null;
	} catch {
		return null;
	}
}

function getCsrfToken(): string {
	if (typeof document === 'undefined') return '';
	const meta = document.querySelector('meta[name="csrf-token"]');
	return meta?.getAttribute('content') ?? '';
}

function scheduleFlush(): void {
	if (flushTimer !== null) return;
	flushTimer = setTimeout(() => {
		flushTimer = null;
		void flush(false);
	}, FLUSH_INTERVAL_MS);
}

async function flush(useBeacon: boolean): Promise<void> {
	if (buffer.length === 0) return;
	const batch = buffer.splice(0, MAX_BATCH);
	const body = JSON.stringify({ events: batch });

	if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
		try {
			navigator.sendBeacon(
				ENDPOINT,
				new Blob([body], { type: 'application/json' })
			);
		} catch {
			// best-effort; sendBeacon may reject if the page is closing too
			// fast for the body to be queued
		}
		return;
	}

	try {
		await fetch(ENDPOINT, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-TOKEN': getCsrfToken(),
				Accept: 'application/json',
			},
			body,
			keepalive: true,
		});
	} catch {
		// Telemetry must not block the app; swallow network errors.
	}
}

function ensurePageHideHandler(): void {
	if (pageHideRegistered || typeof window === 'undefined') return;
	pageHideRegistered = true;
	// pagehide is preferred over beforeunload — it fires reliably on mobile
	// when the user switches apps (which is exactly when the WebRTC peer
	// tends to die).
	window.addEventListener('pagehide', () => void flush(true));
	// Defence-in-depth for desktop browsers that fire beforeunload but not
	// pagehide on some user-action paths.
	window.addEventListener('beforeunload', () => void flush(true));
}

export class StreamingTelemetrySession {
	private readonly ctx: TelemetrySessionContext;

	constructor(ctx: TelemetrySessionContext) {
		this.ctx = ctx;
		ensurePageHideHandler();
	}

	track(event: string, details: Record<string, unknown> = {}): void {
		if (buffer.length >= MAX_BUFFER) {
			// Drop the oldest event to make room — keeps the buffer bounded
			// when the network is unreachable for an extended period.
			buffer.shift();
		}
		buffer.push({
			session_id: this.ctx.sessionId,
			enrollment_test_id: this.ctx.enrollmentTestId,
			actor: this.ctx.actor,
			event,
			at_client: new Date().toISOString(),
			page: getCurrentPage(),
			details,
		});
		scheduleFlush();
	}
}

export function createStreamingTelemetrySession(
	ctx: TelemetrySessionContext
): StreamingTelemetrySession {
	return new StreamingTelemetrySession(ctx);
}

/**
 * Random session ID generator. We don't use `crypto.randomUUID` directly
 * because it isn't available over plain HTTP (only `https://` or
 * `localhost`), and some staging surfaces are still on HTTP.
 */
export function generateSessionId(): string {
	if (
		typeof crypto !== 'undefined' &&
		typeof crypto.randomUUID === 'function'
	) {
		return crypto.randomUUID();
	}
	// Fallback: RFC 4122 v4-style UUID built from Math.random. Not
	// cryptographically secure but fine for a session correlation id.
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}
